import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, type StatTrend } from '@vnetwork/ui/data';
import { useContractStatistical, type ContractListParams, type ContractStatistical } from '@/api/contracts';

/** One stat definition: count key, delta-percent key, label and legend color. */
interface StatDef {
  countKey: keyof ContractStatistical;
  deltaKey: keyof ContractStatistical;
  label: string;
  color: string;
}

const STAT_DEFS: StatDef[] = [
  { countKey: 'total_count', deltaKey: 'delta_total_percent', label: 'Total', color: 'var(--vn-primary-solid)' },
  { countKey: 'created_count', deltaKey: 'delta_created_percent', label: 'Created', color: 'var(--vn-info-solid)' },
  { countKey: 'exchanging_count', deltaKey: 'delta_exchanging_percent', label: 'Exchanging', color: 'var(--vn-info-solid)' },
  { countKey: 'uploading_count', deltaKey: 'delta_uploading_percent', label: 'Uploading', color: 'var(--vn-warning-solid)' },
  { countKey: 'active_count', deltaKey: 'delta_active_percent', label: 'Active', color: 'var(--vn-success-solid)' },
  { countKey: 'expired_count', deltaKey: 'delta_expired_percent', label: 'Expired', color: 'var(--vn-danger-solid)' },
];

/** `12.5` → "▲ 12.5% vs last period"; sign drives the trend coloring. */
function deltaHint(percent: number | undefined): { hint: string; trend: StatTrend } | null {
  if (percent == null || !isFinite(percent) || percent === 0) return null;
  const trend: StatTrend = percent > 0 ? 'up' : 'down';
  const arrow = percent > 0 ? '▲' : '▼';
  return { hint: `${arrow} ${Math.abs(percent).toFixed(2)}% vs last period`, trend };
}

/**
 * Contract summary band: 6 `Stat` cards (Total · Created · Exchanging ·
 * Uploading · Active · Expired) from `useContractStatistical`, each with a
 * period-over-period delta hint. Rendered inside a flat `Card`, mirroring the
 * customer page's stat band.
 *
 * Pass `params` (q + filters from the active list) to scope the counts to the
 * current search/filter state — omit for global totals.
 */
export function ContractStatBand({ params }: { params?: ContractListParams }) {
  const { data, isLoading } = useContractStatistical(params ?? {});
  const stats = data?.result;

  return (
    <Card flat style={{ marginBottom: 'var(--vn-space-4)' }}>
      <CardBody>
        {isLoading ? (
          <StatGroup columns={6}>
            {STAT_DEFS.map((d) => (
              <Skeleton key={d.label} style={{ height: 56 }} />
            ))}
          </StatGroup>
        ) : (
          <StatGroup columns={6}>
            {STAT_DEFS.map((d) => {
              const count = stats?.[d.countKey];
              const delta = deltaHint(stats?.[d.deltaKey]);
              return (
                <Stat
                  key={d.label}
                  label={d.label}
                  dotColor={d.color}
                  value={typeof count === 'number' ? count.toLocaleString('en-US') : '0'}
                  hint={delta?.hint}
                  trend={delta?.trend}
                />
              );
            })}
          </StatGroup>
        )}
      </CardBody>
    </Card>
  );
}
